import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listSubflowTemplates,
  getSubflowTemplate,
  createSubflowTemplate,
  editSubflowTemplate,
  deleteSubflowTemplate,
} from '@/api/subflowTemplates';
import type {
  CreateSubflowTemplateRequestDto,
  EditSubflowTemplateRequestDto,
  SubflowTemplateResponseDto,
  SubflowTemplateType,
} from '@/types/api/subflowTemplate';

export const SUBFLOW_TEMPLATES_QUERY_KEY = 'subflowTemplates';

export const useListSubflowTemplates = (type?: SubflowTemplateType) => {
  return useQuery({
    queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'list', type],
    queryFn: () => listSubflowTemplates(type),
  });
};

export const useGetSubflowTemplate = (id: string) => {
  return useQuery({
    queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'detail', id],
    queryFn: () => getSubflowTemplate(id),
    enabled: !!id,
  });
};

export function useCreateSubflowTemplate() {
  const qc = useQueryClient();

  return useMutation<SubflowTemplateResponseDto, Error, CreateSubflowTemplateRequestDto>({
    mutationFn: createSubflowTemplate,
    onSuccess: (data) => {
      qc.setQueriesData<SubflowTemplateResponseDto[]>({ queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'list'] }, (old) => {
        const prev = old ?? [];
        if (prev.some((i) => i.id === data.id)) return prev;
        return [...prev, data];
      });
    },
  });
}

export function useEditSubflowTemplate() {
  const qc = useQueryClient();

  return useMutation<SubflowTemplateResponseDto, Error, { id: string; dto: EditSubflowTemplateRequestDto }>({
    mutationFn: ({ id, dto }) => editSubflowTemplate(id, dto),
    onSuccess: (data) => {
      qc.setQueriesData<SubflowTemplateResponseDto[]>({ queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'list'] }, (old) =>
        (old ?? []).map((item) => (item.id === data.id ? data : item)),
      );
      qc.setQueryData([SUBFLOW_TEMPLATES_QUERY_KEY, 'detail', data.id], data);
    },
  });
}

export function useDeleteSubflowTemplate() {
  const qc = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteSubflowTemplate,
    onSuccess: (_data, id) => {
      qc.setQueriesData<SubflowTemplateResponseDto[]>({ queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'list'] }, (old) =>
        (old ?? []).filter((item) => item.id !== id),
      );
      qc.removeQueries({ queryKey: [SUBFLOW_TEMPLATES_QUERY_KEY, 'detail', id] });
    },
  });
}
